import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import Header from "../../containers/Header";
import DataTable from "../../containers/DataTable";
import ConfirmDialog from "../../components/ConfirmDialog";
import ButtonsGroup from "../../components/ButtonsGroup";
import AdCreateForm from "../../containers/AdCreateForm";

import { PAGES_TITLES, TITLE_BUTTONS, TITLES } from "../../constants/titles";
import {
  AD_FIELDS_TITLE,
  defaultParamsAdsTable,
  GCP_BUCKET_URL_AVATARS,
} from "../../constants/data";
import {
  GET_ALL_ADS,
  CREATE_AD,
  EDIT_AD,
  DELETE_AD,
  GET_ALL_GOVERNING_BODIES,
} from "../../constants/endpoints";

import { updateDataAds } from "../../utils/getDataForTable";
import { getFillDtoAd } from "../../utils/getFillDto";

import { getAds, deleteAd, createAd, updateAd } from "./sliceReducer";
import { getGoverningBodies } from "../Teams/sliceReducer";
const formatYmd = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return ""; // defensive: bad input
  return d.toISOString().slice(0, 10); // YYYY-MM-DD (UTC)
};

const useStyles = makeStyles({
  root: { display: "flex", justifyContent: "center" },
  progress: { marginTop: "50px" },
});

const ensureMaster = (k) => (/_master$/i.test(k) ? k : `${k}_master`);

export const toGcpUrlIfNeeded = (maybeKey) => {
  const s = (maybeKey || "").toString().trim();
  if (!s) return null;

  // already absolute url (keep as-is)
  if (/^https?:\/\//i.test(s)) return s;

  // object key like "ads/ad_13.png"
  const normalized = s.replace(/^\/+/, "");
  return `${GCP_BUCKET_URL_AVATARS}${ensureMaster(normalized)}`;
};


const Ads = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { fetchDataAds, loading } = useSelector((state) => state.ads);
  const { governingBodies } = useSelector((state) => state.teams);

  const [open, setOpen] = useState(false);
  const [dtos, setDtos] = useState([]);
  const [pagination, setPagination] = useState({ ...defaultParamsAdsTable });
  const [defaultValueForm, setDefaultValueForm] = useState(null);
  const [selectedDto, setSelectedDto] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectID, setSelectID] = useState(null);

  const isEditMode = !isEmpty(selectedDto);

  const governingBodiesTitle = useMemo(() => {
    const list = Array.isArray(governingBodies?.governingBodies)
      ? governingBodies.governingBodies
      : Array.isArray(governingBodies)
      ? governingBodies
      : [];

    return list.map((gb) => ({
      ...gb,
      label: gb.abbreviation || gb.name,
    }));
  }, [governingBodies]);

  const formatRoles = (roles) => {
    if (!Array.isArray(roles)) return "";
    return roles
      .map((r) => (typeof r === "string" ? r : r?.name ?? r?.code ?? r?.id))
      .filter(Boolean)
      .join(", ");
  };
  const tableDtos = useMemo(() => {
    return (dtos || []).map((dto) => ({
      ...dto,
      // ✅ DataTable renders <img> from row.imageUrl
      // imageUrl: resolveImageUrl(dto),
      targetRoles: formatRoles(dto.targetRoles),
      imageUrl: toGcpUrlIfNeeded(dto.imageUrl || dto.imageObjectKey),
      startDate: formatYmd(dto.startDate),
      endDate: formatYmd(dto.endDate),
      creationDate: formatYmd(dto.creationDate),
    }));
  }, [dtos]);

  const handleClickOpen = (data) => {
    setOpen(true);

    if (data?.id) {
      const original = (dtos || []).find((x) => x.id === data.id) || data;

      setSelectedDto(original);
      setDefaultValueForm(original);
    } else {
      setSelectedDto({});
      setDefaultValueForm({ status: "ACTIVE", governingBodies: [] });
    }
  };

  const handleClose = () => {
    setDefaultValueForm(null);
    setOpen(false);
    setSelectedDto({});
  };

  const handleOpenModal = (id) => {
    setSelectID(id);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectID(null);
    setOpenModal(false);
  };

  const reload = (payload = pagination) => {
    return dispatch(
      getAds({
        endpoint: GET_ALL_ADS,
        params: { ...defaultParamsAdsTable, ...payload },
      }),
    );
  };

  const deleteOneAd = async () => {
    if (!selectID) {
      throw new Error("No advertisement selected for deletion.");
    }

    await dispatch(deleteAd(DELETE_AD(selectID))).unwrap();
    await reload();
    toast.success("Advertisement deleted.");
  };

  const onChangePage = (payload) => {
    setPagination((prev) => ({ ...prev, ...payload }));
    reload(payload);
  };

  const onSubmit = async (params) => {
    try {
      if (isEditMode) {
        const old = dtos.find(({ id }) => id === selectedDto.id) || {};

        const data0 = getFillDtoAd({ ...params, id: selectedDto.id }, old);

        // Defensive: never let helper overwrite logoChanged
        const data = { ...data0, logoChanged: params.logoChanged };

        await dispatch(updateAd({ endpoint: EDIT_AD, data })).unwrap();
        await reload();
        toast.success("Advertisement updated.");
        handleClose();
        return;
      }

      const data0 = getFillDtoAd(params, {});

      // Defensive: same for create
      const data = { ...data0, logoChanged: params.logoChanged };

      await dispatch(createAd({ endpoint: CREATE_AD, data })).unwrap();
      await reload();
      toast.success("Advertisement created.");
      handleClose();
    } catch (e) {
      console.error("Ad save failed:", e);
      toast.error("Save failed. Check the API response in Network tab.");
      throw e;
    }
  };

  useEffect(() => {
    if (!isEmpty(fetchDataAds)) {
      const { currentPage, totalElements, adsDto } = fetchDataAds;
      setPagination((s) => ({ ...s, page: currentPage, total: totalElements }));
      setDtos(adsDto || []);
    }
  }, [fetchDataAds]);

  useEffect(() => {
    reload(defaultParamsAdsTable);
    dispatch(getGoverningBodies({ endpoint: GET_ALL_GOVERNING_BODIES }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  console.log("tableDtos[0] =", tableDtos?.[0]);

  return (
    <>
      <Header
        handleClickOpen={handleClickOpen}
        title={PAGES_TITLES.ADS}
        titleButton={TITLE_BUTTONS.CREATE_AD}
      />

      <div className={classes.root}>
        {loading ? (
          <CircularProgress className={classes.progress} />
        ) : (
          <DataTable
            updateItem={handleClickOpen}
            fetchData={tableDtos}
            pagination={pagination}
            removeRow={handleOpenModal}
            onChangePage={onChangePage}
            updateData={updateDataAds}
            fieldsRow={AD_FIELDS_TITLE}
            actions={[
              {
                key: "delete",
                title: "Delete",
                getLabel: () => TITLE_BUTTONS.DELETE,
                onClick: (row) => handleOpenModal(row.id),
              },
              {
                key: "edit",
                title: "Edit",
                getLabel: () => TITLE_BUTTONS.EDIT,
                onClick: (row) => handleClickOpen(row),
              },
            ]}
          />
        )}
      </div>

      {open && (
        <ConfirmDialog
          open
          handleClose={handleClose}
          title={isEditMode ? TITLES.EDIT_AD : TITLES.CREATE_AD}
        >
          {!!defaultValueForm ? (
            <AdCreateForm
              defaultValue={defaultValueForm}
              handleClose={handleClose}
              onSubmit={onSubmit}
              governingBodiesTitle={governingBodiesTitle}
            />
          ) : null}
        </ConfirmDialog>
      )}

      <ConfirmDialog
        open={openModal}
        handleClose={handleCloseModal}
        title={TITLES.DELETE_MODAL}
      >
        <ButtonsGroup
          handleClose={handleCloseModal}
          handleFunction={deleteOneAd}
          submitTitle={TITLE_BUTTONS.DELETE}
          closeTitle={TITLE_BUTTONS.CANCEL}
        />
      </ConfirmDialog>
    </>
  );
};

export default Ads;
